import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from "@angular/forms";
import {
    ChangeOrderModel,
    ChangeOrderResponse,
} from "../../../../core/types/change-order.types";
import { OpportunityService } from "../../../../core/services/opportunity.service";
import { Subject, takeUntil } from "rxjs";
import { PageConfigurationService } from "../../../../core/services/page-configuration.service";
import {
    PageConfiguration,
    PageConfigurationResponse,
} from "../../../../core/types/page-configuration.types";
import { EntityResponse } from "../../../../core/types/types";
import { HttpErrorResponse } from "@angular/common/http";
import { CurrencyPipe, DatePipe, NgFor, NgIf } from "@angular/common";
import { OrderSummaryComponent } from "../../../order-summary/order-summary.component";
import { ModalService } from "../../../../core/services/modal.service";
import { signatureNameValidator } from "../../../../core/validators/signature-name.validator";
import { SignatureInputComponent } from "../../../shared/signature-input/signature-input.component";

import {FormValueSyncDirective} from "../../../../core/directives/form-value-sync.directive";
import {FormValueSyncRegistry} from "../../../../core/directives/form-value-sync.registry";
@Component({
    selector: "app-change-order",
    standalone: true,
    imports: [
        NgIf,
        ReactiveFormsModule,
        FormsModule,
        CurrencyPipe,
        NgFor,
        DatePipe,
        OrderSummaryComponent,
        SignatureInputComponent,
        FormValueSyncDirective
    ],
    templateUrl: "./change-order.component.html",
    styleUrl: "./change-order.component.scss",
})
export class ChangeOrderComponent implements OnInit, OnDestroy {
    private _formValueSync = inject(FormValueSyncRegistry);
    signatureForm: FormGroup | null = null;

    changeOrderDetails: ChangeOrderModel | null = null;
    configuration: PageConfiguration | null = null;

    showSummary: boolean = false;

    signingInProgress: boolean = false;

    private _opportunityService = inject(OpportunityService);
    private _configurationService = inject(PageConfigurationService);
    private _modalService = inject(ModalService);

    private _destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.fetchConfiguration();
        this.fetchChangeOrderDetails();
    }

    createForm(): void {
        this.signatureForm = new FormGroup({
            signedBy: new FormControl(null, [Validators.required, signatureNameValidator()]),
            ip: new FormControl({ value: null, disabled: true }),
            signedOn: new FormControl({ value: null, disabled: true }),
            agree: new FormControl(false, [Validators.requiredTrue]),
        });
    }

    fetchConfiguration(): void {
        this._configurationService.pageConfiguration$
            .pipe(takeUntil(this._destroy$))
            .subscribe((response: PageConfigurationResponse | null) => {
                if (response && response.success) {
                    this.configuration = response.data!;
                }
            });
    }

    fetchChangeOrderDetails(): void {
        this._opportunityService
            .getChangeOrderDetails()
            .pipe(takeUntil(this._destroy$))
            .subscribe((response: EntityResponse<ChangeOrderModel>) => {
                this.changeOrderDetails = response.data;
                if (this.changeOrderDetails.signatureDate) {
                    this.showSummary = true;
                } else {
                    // Only create form if there's no signed document
                    this.createForm();
                    this.signatureForm?.patchValue({
                        ip: this.changeOrderDetails.clientIp,
                        signedOn: new Date(),
                    });
                }
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    submit(): void {
        this._formValueSync.syncAll();

        if (this.signatureForm?.invalid) {
            this.signatureForm?.markAllAsTouched();
            return;
        }

        this.signingInProgress = true;

        const payload = {
            signedBy: this.signatureControls.signedBy.value,
            ip: this.signatureControls.ip.value,
            signedOnTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        this._opportunityService.signChangeOrder(payload).subscribe({
            next: (response: EntityResponse<ChangeOrderResponse>) => {
                this.showSummary = true;
                this.signingInProgress = false;
            },
            error: (error: HttpErrorResponse) => {
                this.signingInProgress = false;
                this.onError(error);
            },
        });
    }

    onError(error: HttpErrorResponse): void {
        this._modalService.showError(error.error?.title, error.error?.message);
    }

    get signatureControls() {
        return {
            signedBy: this.signatureForm?.get("signedBy") as FormControl,
            ip: this.signatureForm?.get("ip") as FormControl,
            signedOn: this.signatureForm?.get("signedOn") as FormControl,
            agree: this.signatureForm?.get("agree") as FormControl,
        };
    }

}
